import {
	AlertBar,
	Button,
	Dialog,
	Form,
	FormattedText,
	FormItem,
	InputRow,
	NumberInput,
	Select,
	SelectItem,
	SelectReadOnly,
	TextArea,
	TextAreaReadOnly,
	TextInput,
	TextInputReadOnly,
	Toolbar,
	useConfirmationDialog,
	ValidationError
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	ReadonlyRefObject,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import {
	isNil,
	isNotNil,
	resolveFormattedTextString
} from "client/Misc/utils";
import { deepComparison } from "client/Misc/compareUtils";
import styles from "./StepDialog.module.less";
import {
	IntegrationFlowAuthenticationOptionModel,
	IntegrationFlowRequestResultModel,
	RequestMethod,
	StepKind
} from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import { IFormHandler, useFormHandler } from "client/Forms";
import { useAuthenticationDialog } from "./AuthenticationDialog/AuthenticationDialog";
import { IObjectInputAttachable, IValueInputAttachable } from "client/Forms/attachable";
import { KeyValueCollection } from "./KeyValueCollection";
import { getClientId, ObjectWithClientId } from "client/Misc/clientId";
import { ClientValidationErrorModel, KeyValuePair } from "client/Api/common";
import {
	convertServerErrors,
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import {
	doesNotExceed,
	doesNotExceedLength,
	isInt32,
	hasMaximumEntries,
	isConvertible,
	isNotEmpty,
	isNotNullNorUndefined,
	isPositive
} from "client/Validation/rules";
import { IObjectValidatorBuilder } from "client/Validation/builder";
import {
	useSynchronizedRef,
	useUnmountedCancellation
} from "client/Hooks";
import {
	methodToTextMap,
	PerItemRequestStepConfiguration,
	RequestStepConfiguration,
	RequestStepKind
} from "./common";
import { useInfoPlaceHoldersDialog } from "./InfoPlaceHoldersDialog";
import { StepResponse } from "./StepResponse";
import { DotPathWildcardPattern } from "client/Misc/dotPathWildcardPattern";
import { Guid } from "client_legacy/ndaw/guid";
import { InputWithSpecialSyntax } from "./InputWithSpecialSyntax";
import { never } from "client_legacy/ndaw/errors";

type StepFormData = RequestStepConfiguration | PerItemRequestStepConfiguration;

export type StepDialogInitialData = {
	kind: "new";
	requestStepKind: RequestStepKind;
} | {
	kind: "edit";
	data: StepFormData;
};

export type StepDialogProps = {
	stepClientId: Guid;
	stepIndex: number;
	authentications: readonly IntegrationFlowAuthenticationOptionModel[];
	initialData: StepDialogInitialData;
	isReadOnly?: boolean;
	stepNames: readonly string[];
	stepResults: Map<Guid, IntegrationFlowRequestResultModel>;
	serverErrors: readonly ClientValidationErrorModel[];
	onDone: (step: StepFormData) => void;
	onClose: () => void;
	onAddAuthentication: (authentication: IntegrationFlowAuthenticationOptionModel) => void;
	onValidate: (newStepData: StepFormData) => Promise<void>;
};

export const StepDialog = (props: StepDialogProps): ReactElement => {
	const {
		stepClientId,
		stepIndex,
		initialData,
		isReadOnly = false,
		authentications,
		stepResults,
		serverErrors,
		stepNames,
		onAddAuthentication,
		onClose,
		onDone,
		onValidate
	} = props;

	const [formData, setFormData] = useState<StepFormData>(
		(): StepFormData => getInitialFormData(initialData)
	);

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [isTriedToValidate, setIsTriedToValidate] = useState<boolean>(false);
	const [hasGeneralError, setHasGeneralError] = useState<boolean>(false);

	const [infoPlaceHoldersDialog, openInfoPlaceHoldersDialog] = useInfoPlaceHoldersDialog();
	const [closeConfirmationDialog, openCloseConfirmationDialog] = useConfirmationDialog();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [authenticationDialog, openAuthenticationDialog] = useAuthenticationDialog();
	const [stepWithErrorName, setStepWithErrorName] = useState<string | null>(null);
	const [shouldShowMapFieldsInfo, setShouldShowMapFieldsInfo] = useState<boolean>(
		(): boolean => {
			switch (initialData.kind) {
				case "new":
					return initialData.requestStepKind === StepKind.PerItemRequest;
				case "edit":
					return initialData.data.kind === StepKind.PerItemRequest;
				default:
					never(initialData);
			}
		}
	);

	const formHandler: IFormHandler<StepFormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const stableFormHandler: ReadonlyRefObject<IFormHandler<StepFormData>> = useSynchronizedRef(formHandler);

	const authenticationOptions = useMemo(
		(): readonly SelectItem<number | null>[] => {
			const result: SelectItem<number | null>[]= authentications
				.map(c => ({
					text: c.name,
					value: c.id
				}));

			result.unshift({
				text: localize("_None"),
				value: null,
				valueTrackingId: "unset"
			});

			return result;
		},
		[authentications]
	);

	const response = useMemo(
		(): IntegrationFlowRequestResultModel | undefined => stepResults.get(stepClientId),
		[
			stepClientId,
			stepResults
		]
	);

	const setServerErrorsHandler = useCallback(
		(errors: readonly ClientValidationErrorModel[]): void => {
			setStepWithErrorName(null);

			stableFormHandler
				.current
				.validation
				.clearServerErrors();

			const currentStepErrors: ClientValidationErrorModel[] = [];
			let errorOnOtherStepIndex: number | null = null;

			for (const error of errors) {
				const match: RegExpMatchArray | null = error
					.propertyName
					.match(/Steps\[(\d+)\]/);

				if (!match) {
					continue;
				}

				const errorIndex: number = parseInt(match[1], 10);

				if (errorIndex === stepIndex) {
					currentStepErrors.push(error);
				} else if (isNil(errorOnOtherStepIndex)) {
					errorOnOtherStepIndex = errorIndex;
				}
			}

			const hasRootError: boolean = currentStepErrors
				.some(
					x => x.propertyName === `Steps[${stepIndex}]`
				);

			setHasGeneralError(hasRootError);

			let pattern: string;

			const {
				kind
			} = stableFormHandler.current
				.value;

			switch (kind) {
				case StepKind.Request:
					pattern = `Steps[${stepIndex}].Request.**`;
					break;
				case StepKind.PerItemRequest:
					pattern = `Steps[${stepIndex}].PerItemRequest.**`;
					break;
				default:
					never(kind);
			}

			stableFormHandler
				.current
				.validation
				.setServerErrors(convertServerErrors(
					currentStepErrors,
					[
						{
							pattern: new DotPathWildcardPattern(pattern),
							replacement: "{0}",
						}
					]
				));

			if (isNotNil(errorOnOtherStepIndex) && stepNames[errorOnOtherStepIndex]) {
				setStepWithErrorName(stepNames[errorOnOtherStepIndex]);
			}
		},
		[
			stableFormHandler,
			stepIndex,
			stepNames
		]
	);

	const createAuthenticationHandler = useCallback(
		async (): Promise<void> => {
			const result: TryResult<IntegrationFlowAuthenticationOptionModel> = await openAuthenticationDialog({});

			if (!result.wasSuccessful) {
				return;
			}

			const {
				result: authentication
			} = result;

			const attachable: IObjectInputAttachable<number | null> = formHandler.for("authenticationId");

			attachable
				.reducer
				.setValue(authentication.id);

			attachable
				.validation
				.triggerInputFinished();

			onAddAuthentication(authentication);
		},
		[
			openAuthenticationDialog,
			onAddAuthentication,
			formHandler
		]
	);

	const validateHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy || isReadOnly) {
				return;
			}

			setIsTriedToValidate(true);
			setIsBusy(true);
			const validationResult: InputValidationResult = await formHandler
				.validation
				.validate(globalCancellation);

			if (validationResult.kind !== InputValidationResultKind.Valid) {
				setIsBusy(false);
				return;
			}

			await onValidate(formData);

			setIsBusy(false);
		},
		[
			formData,
			formHandler.validation,
			globalCancellation,
			isBusy,
			isReadOnly,
			onValidate
		]
	);

	const doneHandler = useCallback(
		async(): Promise<void> => {
			if (!isTriedToValidate || isReadOnly) {
				return;
			}

			const validationResult: InputValidationResult = await formHandler
				.validation
				.validate(globalCancellation);

			if (validationResult.kind !== InputValidationResultKind.Valid) {
				return;
			}

			onDone(formData);
			onClose();
		},
		[
			formData,
			onClose,
			onDone,
			isTriedToValidate,
			isReadOnly,
			formHandler.validation,
			globalCancellation
		]
	);

	const closeHandler = useCallback(
		async (): Promise<void> => {
			const hasChanges: boolean = !deepComparison(formData, getInitialFormData(initialData));

			if (hasChanges) {
				const confirmation: TryResult<void> = await openCloseConfirmationDialog({
					title: localize("_AreYouSureYouWantToLeaveThisPage"),
					confirmText: localize("_DiscardChanges"),
					children: localize("_YouHaveUnsavedChanges"),
					trackingId: "discard-integration-builder-step-form-confirmation-dialog"
				});

				if (!confirmation.wasSuccessful) {
					return;
				}
			}

			onClose();
		},
		[
			formData,
			initialData,
			onClose,
			openCloseConfirmationDialog
		]
	);

	useEffect(
		(): void => {
			// NOTE: Due to a bug in our validation infrastructure if we set server errors
			// directly after user opens the dialog, they will be overwritten by some queue update
			setTimeout(
				() => setServerErrorsHandler(serverErrors),
				1
			);
		},
		[
			serverErrors,
			setServerErrorsHandler
		]
	);

	return (
		<>
			<Dialog
				trackingId="integration-edit-request-step-dialog"
				hasBusyIndicator={isBusy}
				variant="left-slider"
				onClose={closeHandler}
			>
				<Dialog.Header
					title={localize(getDialogTitle(initialData))}
					onShowInfo={() => openInfoPlaceHoldersDialog({})}
					onClose={closeHandler}
					hasBorder
				/>
				<Dialog.Body>
					<div className={styles.container}>
						<div className={styles.leftSide}>
							<Form onSubmit="NONE">
								<FormItem
									label={localize("_Name")}
									isRequired
								>
									{isReadOnly
										? (
											<TextInputReadOnly {...formHandler.for("name").attachToTextInput()} />
										)
										: (
											<>
												<TextInput
													{...formHandler.for("name").attachToTextInput()}
													placeholder={localize("_EnterStepName")}
												/>
												<ValidationError {...formHandler.for("name").attachToValidationError()}/>
											</>
										)
									}
								</FormItem>
								{formHandler.whenType(
									x => x.kind === StepKind.PerItemRequest,
									(handler: IObjectInputAttachable<PerItemRequestStepConfiguration>): ReactElement => {
										const maxItemsHandler: IValueInputAttachable<number | null> = handler.for("maxItems");

										const {
											trackingId,
											value,
										} = maxItemsHandler.attachToTextInput();

										return (
											<>
												<FormItem
													label={localize("_StepCollection")}
													isRequired
												>
													{isReadOnly
														? (
															<TextInputReadOnly
																{...handler.for("stepCollection").attachToTextInput()}
															/>
														)
														: (
															<>
																<InputWithSpecialSyntax kind="expression">
																	<TextInput
																		{...handler.for("stepCollection").attachToTextInput()}
																		placeholder={localize("_EnterStepCollection")}
																	/>
																</InputWithSpecialSyntax>

																<ValidationError
																	{...handler.for("stepCollection").attachToValidationError()}
																/>
															</>
														)
													}
												</FormItem>
												<p className={styles.infoText}>
													<FormattedText
														text={localize("_PerItemRequestCollectionInfo")}
														thingsToWrap={[
															{
																blockId: "Step",
																wrap: (x) => <i>{x}</i>
															}
														]}
													/>
												</p>
												<FormItem
													label={
														resolveFormattedTextString({
															template: localize("_MaximumNumberOfItemsAmount"),
															replacements: [
																{
																	what: "{{Amount}}",
																	filler: DEFAULT_MAX_ITEMS.toString()
																}
															]
														})}
													isRequired
												>
													{isReadOnly
														? (
															<TextInputReadOnly
																value={value?.toString() ?? ""}
																trackingId={trackingId}
															/>
														)
														: (
															<>
																<NumberInput
																	{...maxItemsHandler.attachToTextInput()}
																	placeholder={localize("_EnterMaximumNumberOfItems")}
																/>
																<ValidationError
																	{...maxItemsHandler.attachToValidationError()}
																/>
															</>
														)
													}
												</FormItem>
												<hr className={styles.divider}/>
												{shouldShowMapFieldsInfo && (
													<div className={styles.alert}>
														<AlertBar
															isInline
															kind="info"
															isDismissable
															trackingId="request-collection-info"
															onDismiss={() => setShouldShowMapFieldsInfo(false)}
														>
															<FormattedText
																text={localize("_YouCanAccessValuesOfEachItemInTemplatesInfo")}
																thingsToWrap={[
																	{
																		blockId: "ObjectName",
																		wrap: x => <i>{x}</i>
																	},
																	{
																		blockId: "Format",
																		wrap: x => <i>{x}</i>
																	}
																]}
															/>
														</AlertBar>
													</div>
											)}
											</>
										);
									}
								)}
								<Authentications
									attachable={formHandler}
									authenticationOptions={authenticationOptions}
									onCreateAuthentication={createAuthenticationHandler}
									isReadOnly={isReadOnly}
								/>
								<FormItem
									label={localize("_Method")}
								>
									{isReadOnly
										? (
											<SelectReadOnly
												{...formHandler.for("method").attachToSelect()}
												options={methodOptions}
											/>
										)
										: (
											<>
												<Select
													{...formHandler.for("method").attachToTextInput()}
													options={methodOptions}
													menuTitle={localize("_Method")}
												/>
												<ValidationError {...formHandler.for("method").attachToValidationError()}/>
											</>
										)
									}
								</FormItem>
								<FormItem
									label={localize("_Url")}
									isRequired
								>
									{isReadOnly
										? (
											<TextInputReadOnly {...formHandler.for("url").attachToTextInput()} />
										)
										: (
											<>
												<InputWithSpecialSyntax kind="placholder">
													<TextInput
														{...formHandler.for("url").attachToTextInput()}
														placeholder={localize("_EnterUrl")}
													/>
												</InputWithSpecialSyntax>

												<ValidationError {...formHandler.for("url").attachToValidationError()}/>
											</>
										)
									}
								</FormItem>
								<FormItem
									label={localize("_Headers")}
									isForGroup
								>
									<KeyValueCollection
										attachable={formHandler.for("headers")}
										maxAmount={20}
										isReadOnly={isReadOnly}
									/>

									{!isReadOnly && <ValidationError {...formHandler.for("headers").attachToValidationError(true)} />}
								</FormItem>

								<FormItem
									label={localize("_QueryParameters")}
									isForGroup
								>
									<KeyValueCollection
										attachable={formHandler.for("queryParameters")}
										maxAmount={20}
										isReadOnly={isReadOnly}
									/>

									{!isReadOnly && <ValidationError {...formHandler.for("queryParameters").attachToValidationError(true)} />}
								</FormItem>

								{formHandler.when(
									x => x.method === RequestMethod.Post,
									handler => (
										<FormItem
											label={localize("_Body")}
										>
											{isReadOnly
												? (
													<TextAreaReadOnly
														{...handler.for("body").attachToTextArea()}
														rows={6}
													/>
												)
												: (
													<>
														<InputWithSpecialSyntax kind="placholder">
															<TextArea
																{...handler.for("body").attachToTextArea()}
																rows={6}
															/>
														</InputWithSpecialSyntax>

														<ValidationError {...formHandler.for("body").attachToValidationError()}/>
													</>
												)
											}
										</FormItem>
									)
								)}

							</Form>
						</div>
						<div className={styles.rightSide}>
							<h4 className={styles.resultTitle}>
								{localize("_Result")}
							</h4>

							<Result
								response={response}
								stepWithErrorName={stepWithErrorName}
								hasGeneralError={hasGeneralError}
							/>
						</div>
					</div>
				</Dialog.Body>
				<Dialog.Footer>
					<Toolbar>
						{!isReadOnly && (
							<>
								<Toolbar.Button
									kind="primary"
									text={localize("_Validate")}
									onClick={validateHandler}
									trackingId="validate"
								/>
								<Toolbar.Button
									kind="primary"
									text={localize("_Done")}
									onClick={doneHandler}
									trackingId="done"
									isDisabled={!isTriedToValidate}
								/>
							</>
						)}
						<Toolbar.Button
							kind="secondary"
							text={localize(isReadOnly
								? "_Close"
								: "_Cancel"
							)}
							trackingId="cancel"
							onClick={closeHandler}
						/>
					</Toolbar>
				</Dialog.Footer>
			</Dialog>
			{infoPlaceHoldersDialog}
			{authenticationDialog}
			{closeConfirmationDialog}
		</>
	);
};
StepDialog.displayName = "StepDialog";

function getDialogTitle(
	initialData: StepDialogInitialData
): LocalizationKey {
	const isEdit: boolean = initialData.kind === "edit";
	const stepKind: RequestStepKind = initialData.kind === "edit"
		? initialData.data.kind
		: initialData.requestStepKind;

	switch (stepKind) {
		case StepKind.Request:
			return isEdit
				? "_EditStep"
				: "_CreateNewStep";
		case StepKind.PerItemRequest:
			return isEdit
				? "_EditPerItemStep"
				: "_CreateNewPerItemStep";
		default:
			never(stepKind);
	}
}

const getInitialFormData = (initialData: StepDialogInitialData): StepFormData => {
	if (initialData.kind === "edit") {
		return initialData.data;
	}

	switch (initialData.requestStepKind) {
		case StepKind.Request:
			return {
				kind: StepKind.Request,
				name: "",
				authenticationId: null,
				method: RequestMethod.Get,
				url: "",
				headers: [],
				queryParameters: [],
				body: ""
			};
		case StepKind.PerItemRequest:
			return {
				kind: StepKind.PerItemRequest,
				name: "",
				authenticationId: null,
				method: RequestMethod.Get,
				url: "",
				headers: [],
				queryParameters: [],
				body: "",
				stepCollection: "",
				maxItems: null
			};
		default:
			never(initialData.requestStepKind);
	}
};

const DEFAULT_MAX_ITEMS = 20;

const validationConfiguration: IValidationConfiguration<StepFormData> = createValidationConfiguration<StepFormData>();

validationConfiguration.whenType(x => x.kind === StepKind.PerItemRequest)
	.forScalar("maxItems")
	.add(
		isConvertible(),
		isNotNullNorUndefined(),
		doesNotExceed(DEFAULT_MAX_ITEMS),
		isPositive(),
		isInt32()
	);

validationConfiguration.whenType(x => x.kind === StepKind.PerItemRequest)
	.forScalar("stepCollection")
	.add(
		isNotEmpty(),
		isNotNullNorUndefined()
	);

validationConfiguration.forScalar("name")
	.add(
		isNotEmpty(),
		isNotNullNorUndefined(),
		doesNotExceedLength(255)
	);

validationConfiguration.forScalar("url")
	.add(
		isNotEmpty(),
		isNotNullNorUndefined(),
		doesNotExceedLength(2_048)
	);

const headersConfiguration: IObjectValidatorBuilder<ObjectWithClientId<KeyValuePair<string, string>>> = validationConfiguration
	.forCollection("headers")
	.add(
		hasMaximumEntries(20)
	)
	.forItems(getClientId);

const queryParametersConfiguration: IObjectValidatorBuilder<ObjectWithClientId<KeyValuePair<string, string>>> = validationConfiguration
	.forCollection("queryParameters")
	.add(
		hasMaximumEntries(20)
	)
	.forItems(getClientId);

headersConfiguration.forScalar("key")
	.add(
		isNotEmpty(),
		doesNotExceedLength(255)
);

headersConfiguration.forScalar("value")
	.add(
		doesNotExceedLength(1_024)
	);

queryParametersConfiguration.forScalar("key")
	.add(
		isNotEmpty(),
		doesNotExceedLength(255)
);

queryParametersConfiguration.forScalar("value")
	.add(
		doesNotExceedLength(1_024)
	);

validationConfiguration.when(x => x.method === RequestMethod.Post)
	.forScalar("body")
	.add(
		doesNotExceedLength(8_192)
	);

const methodOptions: readonly SelectItem<RequestMethod>[] = [
	{
		value: RequestMethod.Get,
		text: methodToTextMap[RequestMethod.Get]
	},
	{
		value: RequestMethod.Post,
		text: methodToTextMap[RequestMethod.Post]
	}
];

type AuthenticationsProps = {
	attachable: IValueInputAttachable<StepFormData>;
	authenticationOptions: readonly SelectItem<number | null>[];
	onCreateAuthentication(): void;
	isReadOnly: boolean;
};

const Authentications = (props: AuthenticationsProps): ReactElement => {
	const {
		attachable,
		authenticationOptions,
		isReadOnly,
		onCreateAuthentication
	} = props;

	return (
		<>
			<FormItem
				label={localize("_Authentication")}
			>
				{isReadOnly
					? (
						<SelectReadOnly
							{...attachable.for("authenticationId").attachToSelect()}
							options={authenticationOptions}
						/>
					)
					: (
						<>
							<InputRow>
								<InputRow.Stretch>
									<Select
										{...attachable.for("authenticationId").attachToSelect()}
										options={authenticationOptions}
										menuTitle={localize("_Authentication")}
										placeholder={localize("_SelectAuthentication")}
									/>
								</InputRow.Stretch>
								<Button
									kind="secondary"
									text={localize("_New")}
									onClick={onCreateAuthentication}
									trackingId="create-new-authentication-button"
								/>
							</InputRow>
							<ValidationError {...attachable.for("authenticationId").attachToValidationError()}/>
						</>
					)
				}
			</FormItem>
		</>
	);
};
Authentications.displayName = "Authentications";

export type ResultProps = {
	stepWithErrorName: string | null;
	response?: IntegrationFlowRequestResultModel;
	hasGeneralError: boolean;
};

export const Result = (props: ResultProps): ReactElement | null => {
	const {
		response,
		stepWithErrorName,
		hasGeneralError
	} = props;

	if (hasResponse(response)) {
		return (
			<StepResponse
				response={response}
			/>
		);
	}

	if (hasGeneralError) {
		return (
			<div className={styles.resultError}>
				{localize("_CouldNotPerformTheRequestPleaseCheckConfiguration")}
			</div>
		);
	}

	if (isNotNil(stepWithErrorName)) {
		return (
			<div className={styles.resultError}>
				<FormattedText
					text={localize("_ErrorsFoundInTheFollowingStep")}
					thingsToReplace={[
						{
							what: "{{StepName}}",
							filler: stepWithErrorName
						}
					]}
				/>
			</div>
		);
	}

	return null;
};
Result.displayName = "Result";

function hasResponse(response?: IntegrationFlowRequestResultModel): response is IntegrationFlowRequestResultModel {
	if (!response) {
		return false;
	}

	return isNotNil(response.content)
		|| isNotNil(response.statusCode)
		|| isNotNil(response.reasonPhrase)
		|| response.isBinaryContent;
}